import { useApolloClient } from '@apollo/client';
import { isNil } from 'lodash/fp';
import { useCallback, useEffect, useState } from 'react';
// eslint-disable-next-line max-len
import { type MobilityApplicationEntrypointContextDataFragment } from '../../../../api/fragments/MobilityApplicationEntrypointContextData';
import { ApplicationKind } from '../../../../api/types';
import ApplicationSessionModal from '../../../../components/ApplicationSessionExpireModal';
import { computeMobilityGTMData, MobilityGTMEvents } from '../../../../utilities/googleTagManager';
import { usePersistentData } from '../../../../utilities/usePersistData';
import useEndSession from '../../../shared/useEndSession';
import LiveChat from '../../MobilityWebpageEndpoint/Webpage/LiveChat';
import {
    mobilityJourneyTemporaryKey,
    type MobilityJourneyTemporaryValueType,
    useHandleTemporarySession,
} from '../helper';
import CarListingInner from './CarListingInner';
import CarListingStateProvider from './CarListingStateProvider';

type CarListingPageProps = {
    endpoint: MobilityApplicationEntrypointContextDataFragment;
};

const CarListingPage = ({ endpoint }: CarListingPageProps) => {
    const apolloClient = useApolloClient();
    const { getItem, setItem, deleteItem } = usePersistentData();
    const { extendTemporarySession } = useHandleTemporarySession();
    const [mobilityJourneyTemporaryValue, setMobilityJourneyTemporaryValue] = useState<
        MobilityJourneyTemporaryValueType | undefined
    >();

    useEffect(() => {
        setMobilityJourneyTemporaryValue(getItem(mobilityJourneyTemporaryKey));
    }, [getItem]);

    const handleExtendSession = useCallback(async () => {
        await extendTemporarySession(apolloClient, setItem, mobilityJourneyTemporaryValue);
        setMobilityJourneyTemporaryValue(getItem(mobilityJourneyTemporaryKey));
    }, [apolloClient, extendTemporarySession, getItem, mobilityJourneyTemporaryValue, setItem]);

    const endSession = useEndSession();

    useEffect(() => {
        if ((window?.dataLayer || []).some(data => data.event !== MobilityGTMEvents.VehicleSelectionView)) {
            const mobilityGTMData = computeMobilityGTMData(MobilityGTMEvents.VehicleSelectionView);
            window.dataLayer?.push(mobilityGTMData);
        }
    }, []);

    const handleEndSession = useCallback(async () => {
        if (mobilityJourneyTemporaryValue?.token) {
            await endSession(mobilityJourneyTemporaryValue.token, ApplicationKind.Mobility);
            deleteItem(mobilityJourneyTemporaryKey);
        }
    }, [deleteItem, endSession, mobilityJourneyTemporaryValue?.token]);

    return (
        <>
            {mobilityJourneyTemporaryValue?.token && (
                <ApplicationSessionModal
                    endSession={handleEndSession}
                    expireAt={mobilityJourneyTemporaryValue.expireAt}
                    extendSession={handleExtendSession}
                    redirectPath="."
                />
            )}

            <CarListingStateProvider endpoint={endpoint}>
                <CarListingInner />
                {!isNil(endpoint.mobilityApplicationModule.liveChatSetting) && (
                    <LiveChat
                        chatSetting={
                            endpoint.mobilityApplicationModule.liveChatSetting.__typename ===
                                'UserlikeChatbotSetting' && endpoint.mobilityApplicationModule.liveChatSetting
                        }
                        hasFooter={false}
                    />
                )}
            </CarListingStateProvider>
        </>
    );
};

export default CarListingPage;
